import React, { useState, useEffect } from 'react';
import type { Player, PlayerCreate, PlayerUpdate } from '../services/api';
import { getPlayers, createPlayer, updatePlayer, deletePlayer } from '../services/api';
import eventBus from '../utils/eventBus';
import axios from 'axios';
import {
    Box, Typography, Button, TextField,
    Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
    IconButton, CircularProgress, Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add'; // For 'Add New Player' button
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // Import help icon
import Modal from './Modal'; // Import Modal for the help dialog
import AlgorithmSettings from './AlgorithmSettings'; // Import settings component


const PlayerList: React.FC = () => { // Changed from React.FC<PlayerListProps>
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [newPlayerData, setNewPlayerData] = useState<PlayerCreate>({ name: '' });
  
  const [editingPlayerId, setEditingPlayerId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<PlayerUpdate>({});
  const [isHelpModalOpen, setHelpModalOpen] = useState(false); // State for help modal
  const [isSettingsModalOpen, setSettingsModalOpen] = useState(false); // State for settings modal

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const data = await getPlayers();
      setPlayers(data);
      setError(null);
    } catch (_err) { // Changed 'err' to '_err'
      setError('Failed to fetch players.');
      console.error(_err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
    
    const handleDataChange = () => fetchPlayers();
    eventBus.on('dataChanged', handleDataChange);
    
    return () => {
      eventBus.remove('dataChanged', handleDataChange);
    };
  }, []);

  const handleAddClick = () => {
    setIsAdding(true);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewPlayerData({ name: '' });
    setError(null);
  };

  const handleCreatePlayer = async () => {
    const trimmedName = newPlayerData.name.trim();
    if (!trimmedName) {
        setError("Name is required.");
        return;
    }

    // Client-side check for duplicates
    const isDuplicate = players.some(p => p.name.toLowerCase() === trimmedName.toLowerCase());
    if (isDuplicate) {
        setError(`Player with name "${trimmedName}" already exists.`);
        return;
    }

    try {
        await createPlayer({ name: trimmedName, rating_mu: newPlayerData.rating_mu ? Number(newPlayerData.rating_mu) : undefined }); // rating_mu can be undefined
        setNewPlayerData({ name: '' });
        eventBus.dispatch('dataChanged');
    } catch (_err: unknown) { // Changed 'any' to 'unknown'
        // Handle backend validation error as well
        if (axios.isAxiosError(_err) && _err.response && _err.response.status === 400) {
            setError(_err.response.data.detail);
        } else {
            setError("Failed to create player.");
        }
        console.error(_err);
    }
  };

  const handleEditClick = (player: Player) => {
    setEditingPlayerId(player.id);
    setEditingData({ name: player.name, rating_mu: player.rating_mu });
  };

  const handleCancelEdit = () => {
    setEditingPlayerId(null);
    setEditingData({});
  };

  const handleSaveEdit = async (id: number) => {
    const trimmedName = editingData.name?.trim();
    if (!trimmedName) {
        setError("姓名不得為空。");
        return;
    }
    try {
      await updatePlayer(id, { name: trimmedName, rating_mu: editingData.rating_mu ? Number(editingData.rating_mu) : undefined });
      setEditingPlayerId(null);
      eventBus.dispatch('dataChanged');
    } catch (_err) { // Changed 'err' to '_err'
      setError(`Failed to update player ${id}.`);
      console.error(_err);
    }
  };

  const handleDeleteClick = async (id: number) => {
    if (window.confirm('確定要刪除這位球員嗎？')) {
        try {
            await deletePlayer(id);
            eventBus.dispatch('dataChanged');
        } catch (_err) { // Changed 'err' to '_err'
            setError(`Failed to delete player ${id}.`);
            console.error(_err);
        }
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
    </Box>
  );
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            管理球員
          </Typography>
          <IconButton onClick={() => setHelpModalOpen(true)} size="small" sx={{ color: 'primary.main' }}>
            <HelpOutlineIcon />
          </IconButton>
        </Box>
        <Box sx={{ mt: 2 }}>
        {!isAdding && (
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={handleAddClick} 
                sx={{ mb: 2 }}
            >
                新增球員
            </Button>
        )}
        {isAdding && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h6">新球員</Typography>
              <TextField 
                  label="姓名"
                  variant="outlined"
                  size="small"
                  value={newPlayerData.name}
                  onChange={(e) => setNewPlayerData({...newPlayerData, name: e.target.value})}
                  fullWidth
              />
              <TextField
                  label="初始積分 (例如: 1500)"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={newPlayerData.rating_mu || ''}
                  onChange={(e) => setNewPlayerData({...newPlayerData, rating_mu: Number(e.target.value)})}
                  fullWidth
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleCreatePlayer}>儲存</Button>
                <Button variant="outlined" onClick={handleCancelAdd}>取消</Button>
              </Box>
          </Box>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>姓名</TableCell>
                <TableCell>積分</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {players.map((player) => (
                <TableRow key={player.id}>
                  {editingPlayerId === player.id ? (
                    <>
                        <TableCell>
                            <TextField 
                                value={editingData.name || ''} 
                                onChange={(e) => setEditingData({...editingData, name: e.target.value})} 
                                variant="outlined" 
                                size="small"
                                fullWidth
                            />
                        </TableCell>
                        <TableCell>
                            <TextField 
                                value={editingData.rating_mu || ''} 
                                onChange={(e) => setEditingData({...editingData, rating_mu: Number(e.target.value)})} 
                                variant="outlined" 
                                size="small"
                                type="number"
                                fullWidth
                            />
                        </TableCell>
                        <TableCell align="right">
                            <Button 
                                variant="contained" 
                                size="small" 
                                onClick={() => handleSaveEdit(player.id)}
                                sx={{ mr: 1 }}
                            >
                                儲存
                            </Button>
                            <Button 
                                variant="outlined" 
                                size="small" 
                                onClick={handleCancelEdit}
                            >
                                取消
                            </Button>
                        </TableCell>
                    </>
                  ) : (
                    <>
                        <TableCell>{player.name}</TableCell>
                        <TableCell>{player.rating_mu.toFixed(0)}</TableCell>
                        <TableCell align="right">
                            <IconButton 
                                aria-label="edit" 
                                onClick={() => handleEditClick(player)}
                                color="primary"
                            >
                                <EditIcon />
                            </IconButton>
                            <IconButton 
                                aria-label="delete" 
                                onClick={() => handleDeleteClick(player.id)}
                                color="error"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>

    {/* Help Modal */}
    <Modal isOpen={isHelpModalOpen} onClose={() => setHelpModalOpen(false)} title="排點演算法說明">
      <Typography variant="h6" gutterBottom>核心演算法：基因演算法</Typography>
      <Typography paragraph>
        為了在速度與效果間取得平衡，系統採用「基因演算法」來尋找最佳的對戰組合，而非計算所有可能性。它模仿生物演化的「適者生存」原則，快速地「進化」出一個優質的組合。
      </Typography>
      <Typography variant="h6" gutterBottom>三大評分維度</Typography>
      <Typography component="div" paragraph>
        <ul>
          <li><strong>實力評分 (Quality):</strong> 系統會盡力讓兩隊的總積分相近，實力相當的比賽才最精彩。</li>
          <li><strong>公平性 (Fairness):</strong> 系統會考量到已連續等待多次的球員，讓他們有更高的上場優先序，避免有人一直坐冷板凳。</li>
          <li><strong>多樣性 (Variety):</strong> 系統會參考歷史紀錄，盡量避免讓某些人一直搭檔，或一直當對手，以增加打球的趣味性。</li>
        </ul>
      </Typography>
      <Typography variant="h6" gutterBottom>實力評分設定原則</Typography>
      <Typography paragraph component="div">
        本系統的積分類似於國際象棋的「埃洛等級分 (Elo Rating)」，代表玩家的相對實力。一個好的起點是將團隊的**平均水平設定在 1500 分**。
        <ul>
            <li><strong>職業/公開賽級別 (A級):</strong> 2000+ 分</li>
            <li><strong>進階/校隊級別 (B級):</strong> 1700 - 1999 分</li>
            <li><strong>中階/普通愛好者 (C級):</strong> 1400 - 1699 分</li>
            <li><strong>初學者 (D級):</strong> 1100 - 1399 分</li>
            <li><strong>新手 (第一次打球):</strong> 1000 分</li>
        </ul>
        系統會根據每場比賽的結果自動調整積分。贏了強的對手，分數會大幅增加；輸給弱的對手，分數會大幅減少。
      </Typography>
      <Typography variant="h6" gutterBottom>綜合範例</Typography>
      <Typography paragraph>
        假設 A 球員 (積分2000) 已連打三場，而 B 球員 (積分1000) 已等待三輪。當系統尋找下一場的組合時，雖然 A 球員的實力較強，但演算法會因為 B 球員的「公平性」分數很高，而傾向於將 B 球員排入下一場對戰中。
      </Typography>
      <Box mt={2} textAlign="right">
        <Button variant="contained" onClick={() => { setHelpModalOpen(false); setSettingsModalOpen(true); }}>
          進階設定
        </Button>
      </Box>
    </Modal>

    {/* Settings Modal */}
    <Modal isOpen={isSettingsModalOpen} onClose={() => setSettingsModalOpen(false)} title="演算法參數設定">
      <AlgorithmSettings />
    </Modal>
  </>
);
};

export default PlayerList;
